// THE PEOPLE FIELD (owner, 2026-10-09 — D-341: "For Assignments, do the People
// Field."). Every Assignments pop-up edits its list of people through this one
// control, which works like the To line in Outlook: everyone who holds the role
// is a name tag inside one box, typing in the same box searches staff and adds
// another, and the ✕ on a tag takes that person off.
//
// IT HOLDS NOTHING AND WRITES NOTHING. The caller owns the staged changes and
// the Apply that writes them; this draws them. A new person is a blue tag; a
// removed one leaves the box and is named underneath with an Undo, so a
// mistaken ✕ costs one click rather than a re-search.
//
// A PARTIAL TAG belongs to the bulk pop-up: someone who holds only some of the
// selected roles. It is dashed and says "2 of 5"; clicking its name adds the
// person to the rest, and its ✕ takes them off the ones they hold.
import { useState } from 'react'
import { Button, Caption1, Label, Tooltip, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'
import { EntityPicker, type EntityOption } from './EntityPicker'

export interface PersonTag {
  key: string
  name: string
  /** The hover line: email, and when the assignment began where it has a date. */
  detail?: string
  /** `held` today, `new` staged to add, `partial` held on some selected roles. */
  tone: 'held' | 'new' | 'partial'
  /** "2 of 5", on a partial tag. */
  count?: string
  /** False where the viewer may not remove this person: no ✕. */
  removable: boolean
  /** Partial tags only: add the person to every selected role. */
  onComplete?: () => void
}

interface Props {
  /** The field label: the row's own name on a one-row pop-up (owner,
      2026-10-11 — D-348), a list's name where a pop-up has several. Absent on
      Edit N Selected, which has no one name; `ariaLabel` then names the box. */
  label?: string
  /** The box's accessible name when it differs from the label, or has none. */
  ariaLabel?: string
  tags: readonly PersonTag[]
  /** Staged removals, named under the box with an Undo each. */
  removing: readonly { key: string; name: string }[]
  onRemove: (key: string) => void
  onUndo: (key: string) => void
  search: (term: string) => Promise<EntityOption[]>
  onAdd: (option: EntityOption) => void
  mountNode?: HTMLElement | null
  disabled?: boolean
  /** No picker at all, for a viewer who may not add anyone. */
  readOnly?: boolean
}

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 },
  // Drawn as one text box: the same border, radius and darker bottom edge a
  // Fluent input has, with the tags and the search inside it.
  box: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px',
    minHeight: '32px',
    padding: '3px 4px',
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    borderBottomColor: tokens.colorNeutralStrokeAccessible,
    borderRadius: tokens.borderRadiusMedium,
    ':focus-within': { borderBottomColor: tokens.colorCompoundBrandStroke, borderBottomWidth: '2px', paddingBottom: '2px' },
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    maxWidth: '100%',
    padding: '1px 2px 1px 10px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    ...shorthands.border('1px', 'solid', 'transparent'),
    whiteSpace: 'nowrap',
  },
  tagNoRemove: { paddingRight: '10px' },
  tagNew: { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2 },
  tagPartial: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke1Hover),
  },
  name: { overflow: 'hidden', textOverflow: 'ellipsis' },
  // The partial tag's name is the "add to the rest" control.
  complete: {
    background: 'none',
    ...shorthands.border('0'),
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    ':hover': { textDecorationLine: 'underline' },
  },
  count: { marginLeft: '6px', color: tokens.colorNeutralForeground3 },
  x: { minWidth: '20px', width: '20px', height: '20px', padding: 0, borderRadius: tokens.borderRadiusCircular, color: 'inherit' },
  // The search sits in the box, borderless. Its SLOT takes whatever width the
  // tags leave on the last line; the combobox itself sets no flex size, because
  // it sits in the picker's own column and a basis there is a height.
  picker: {
    ...shorthands.borderWidth('0'),
    backgroundColor: 'transparent',
    '::after': { display: 'none' },
    ':hover': { ...shorthands.borderWidth('0') },
    ':focus-within': { ...shorthands.borderWidth('0') },
  },
  pickerSlot: { flex: '1 1 160px', minWidth: '120px' },
  removing: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: '10px', color: tokens.colorNeutralForeground3 },
  undo: {
    minWidth: 0,
    height: 'auto',
    padding: 0,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorBrandForegroundLink,
    ':hover': { color: tokens.colorBrandForegroundLinkHover, textDecorationLine: 'underline' },
  },
})

export function PeopleField({
  label,
  ariaLabel,
  tags,
  removing,
  onRemove,
  onUndo,
  search,
  onAdd,
  mountNode,
  disabled,
  readOnly,
}: Props) {
  const s = useStyles()
  // A fresh picker after each pick, so the box is empty for the next name.
  const [generation, setGeneration] = useState(0)
  const name = ariaLabel ?? label ?? 'People'
  return (
    <div className={s.wrap} role="group" aria-label={name}>
      {/* Fluent's own `Label`, which is what a `Field` draws above every other
          input, so this header reads like theirs (owner, 2026-10-09). */}
      {label && <Label>{label}</Label>}
      <div className={s.box}>
        {tags.map((t) => (
          <Tooltip key={t.key} content={tagTooltip(t)} relationship="description" withArrow>
            <span
              className={mergeClasses(
                s.tag,
                t.tone === 'new' && s.tagNew,
                t.tone === 'partial' && s.tagPartial,
                !t.removable && s.tagNoRemove,
              )}
            >
              {t.onComplete ? (
                <button
                  type="button"
                  className={mergeClasses(s.complete, s.name)}
                  disabled={disabled}
                  onClick={t.onComplete}
                  aria-label={`Add ${t.name} to every selected role`}
                >
                  {t.name}
                </button>
              ) : (
                <span className={s.name}>{t.name}</span>
              )}
              {t.count && <Caption1 className={s.count}>{t.count}</Caption1>}
              {t.removable && (
                <Button
                  appearance="transparent"
                  size="small"
                  className={s.x}
                  icon={<DismissRegular fontSize={12} />}
                  disabled={disabled}
                  aria-label={`Remove ${t.name}`}
                  onClick={() => onRemove(t.key)}
                />
              )}
            </span>
          </Tooltip>
        ))}
        {!readOnly && (
          <div className={s.pickerSlot}>
            <EntityPicker
              key={generation}
              className={s.picker}
              // Help text only while the box is empty (owner, 2026-10-09: "we do
              // not need help text when users are already selected").
              placeholder={tags.length === 0 ? 'e.g. colleague@pace-cs.com' : ''}
              ariaLabel={`Add to ${name}`}
              mountNode={mountNode}
              disabled={disabled}
              clearButton={false}
              search={search}
              onSelect={(option) => {
                onAdd(option)
                setGeneration((n) => n + 1)
              }}
            />
          </div>
        )}
      </div>
      {removing.length > 0 && (
        <div className={s.removing}>
          {removing.map((r) => (
            <Caption1 key={r.key}>
              Removing {r.name}.{' '}
              <Button appearance="transparent" className={s.undo} onClick={() => onUndo(r.key)} disabled={disabled}>
                Undo
              </Button>
            </Caption1>
          ))}
        </div>
      )}
    </div>
  )
}

/** What hovering a tag says. */
function tagTooltip(t: PersonTag): string {
  const lines = [t.detail, t.tone === 'new' ? 'Will be added when you save.' : undefined]
  if (t.onComplete) lines.push(`Holds ${t.count}.`)
  return lines.filter(Boolean).join(' · ') || t.name
}
